import { Head } from "@inertiajs/react";
import DashboardLayout from "@/layout/dashboard-layout";
import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Users, 
  Shield, 
  Settings, 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Key,
  UserPlus,
  ShieldCheck
} from "lucide-react";
import { router } from "@inertiajs/react";
import UserManagement from "./components/user-management.jsx";
import RoleManagement from "./components/role-management.jsx";
import PermissionMatrix from "./components/permission-matrix.jsx";

type User = {
  id: number;
  name: string;
  email: string;
  status: string;
  profile: string;
  profile_text: string;
  posts_count: number;
  published_posts_count: number;
  created_at: string;
  roles: Role[];
};

type Role = {
  id: number;
  name: string;
  description: string;
  is_system_role: boolean;
  permissions: Permission[];
};

type Permission = {
  id: number;
  name: string;
  resource: string;
  action: string;
  description: string;
};

type Stats = {
  total_users: number;
  active_users: number;
  total_blogs: number;
  total_posts: number;
  unresolved_alerts: number;
};

type Workspace = {
  id: number;
  name: string;
  created_at: string;
  total_blogs_count: number;
  active_blogs_count: number;
  total_posts_count: number;
  published_posts_count: number;
};

type SettingsIndexProps = {
  users: User[];
  roles: Role[];
  permissions: Record<string, Permission[]>;
  user_profiles: Array<[string, string]>;
  workspace: Workspace;
  stats: Stats;
};

export default function SettingsIndex({ 
  users, 
  roles, 
  permissions, 
  user_profiles, 
  workspace, 
  stats 
}: SettingsIndexProps) {
  const [activeTab, setActiveTab] = useState("users");

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="default" className="bg-green-500">Ativo</Badge>;
      case 'inactive':
        return <Badge variant="secondary">Inativo</Badge>;
      case 'pending':
        return <Badge variant="outline">Pendente</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <DashboardLayout>
      <Head title="Configurações" />
      
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Configurações</h1>
            <p className="text-muted-foreground">
              Gerencie usuários, permissões e configurações do workspace
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total de Usuários</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_users}</div>
              <p className="text-xs text-muted-foreground">
                {stats.active_users} ativos
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Roles Customizados</CardTitle>
              <Shield className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {roles.filter(role => !role.is_system_role).length}
              </div>
              <p className="text-xs text-muted-foreground">
                {roles.filter(role => role.is_system_role).length} do sistema
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Blogs Ativos</CardTitle>
              <Settings className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{workspace.active_blogs_count}</div>
              <p className="text-xs text-muted-foreground">
                de {workspace.total_blogs_count} total
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Posts Publicados</CardTitle>
              <ShieldCheck className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{workspace.published_posts_count}</div>
              <p className="text-xs text-muted-foreground">
                de {workspace.total_posts_count} total
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Usuários
            </TabsTrigger>
            <TabsTrigger value="roles" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Roles
            </TabsTrigger>
            <TabsTrigger value="permissions" className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" />
              Permissões
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="space-y-4">
            <UserManagement 
              users={users}
              roles={roles}
              userProfiles={user_profiles}
              getStatusBadge={getStatusBadge}
            />
          </TabsContent>

          <TabsContent value="roles" className="space-y-4">
            <RoleManagement 
              roles={roles}
              permissions={permissions}
            />
          </TabsContent>

          <TabsContent value="permissions" className="space-y-4">
            <PermissionMatrix 
              roles={roles}
              permissions={permissions}
            />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}